import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import { Breadcrumb, SectionHeading, TeamBadge, FormDots } from "@/components/base/Ui";
import { CategoryTag, EventRow, StatusTag } from "@/components/base/EventCard";
import { MetricBar } from "@/components/base/BlacklistRow";
import { LoadingState, ErrorState } from "@/components/base/StateViews";
import { getBlacklistByEvent, getCategoryById, relatedEvents, formatDate, statusLabels } from "@/utils/data";
import { events } from "@/mocks/events";
import type { EventItem, BlacklistEntry } from "@/types";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

export default function DetailPage() {
  const { eventId = "" } = useParams();
  const event = (events as EventItem[]).find((e) => e.id === eventId);
  const [loading, setLoading] = useState(true);

  useDocumentMeta({
    title: event ? `${event.title} - 51-每日大赛` : "赛事详情 - 51-每日大赛",
    description: event
      ? `${event.title}详情。比赛时间 ${event.date} ${event.time}，地点 ${event.location}。${event.summary}`
      : "51-每日大赛赛事详情页。",
    keywords: event ? `${event.title},${event.tags.join(",")},赛事资讯` : "赛事详情,51-每日大赛",
  });

  useEffect(() => {
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 400);
    return () => clearTimeout(timer);
  }, [eventId]);

  if (loading) {
    return (
      <Layout>
        <div className="mx-auto w-full px-4 py-8 md:px-6">
          <LoadingState label="正在加载赛事详情…" />
        </div>
      </Layout>
    );
  }

  if (!event) {
    return (
      <Layout>
        <div className="mx-auto w-full px-4 py-8 md:px-6">
          <ErrorState
            title="赛事不存在（404）"
            desc="你访问的赛事详情不存在或链接已失效，返回首页继续浏览其他赛事资讯。"
            actionLabel="返回首页"
            onAction={() => window.REACT_APP_NAVIGATE("/")}
          />
        </div>
      </Layout>
    );
  }

  const category = getCategoryById(event.categoryId);
  const blackEntry = getBlacklistByEvent(event.id) as BlacklistEntry | undefined;
  const related = relatedEvents(event.id, event.categoryId, 3) as EventItem[];

  return (
    <Layout>
      <div className="mx-auto w-full px-4 py-8 md:px-6">
        <Breadcrumb
          items={[
            { label: "首页", to: "/" },
            { label: `${category?.name ?? ""}资讯`, to: `/category/${event.categoryId}` },
            { label: "赛事详情" },
          ]}
        />

        <div className="mt-5 grid grid-cols-1 gap-6 lg:grid-cols-[1.7fr_1fr]">
          <article className="min-w-0">
            <header className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <div className="relative h-52 w-full overflow-hidden bg-background-200 sm:h-72 md:h-80">
                <img
                  src={event.cover}
                  alt={event.title}
                  title={`${event.title} 赛事详情`}
                  className="h-full w-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-950/85 via-primary-950/20 to-transparent"></div>
                <div className="absolute inset-x-0 bottom-0 p-5 md:p-6">
                  <div className="flex flex-wrap items-center gap-2">
                    <CategoryTag categoryId={event.categoryId} />
                    <StatusTag status={event.status} />
                    <span className="rounded-full bg-background-50/20 px-2.5 py-0.5 text-xs font-medium text-background-100">
                      {statusLabels[event.status]}
                    </span>
                  </div>
                  <h1 className="mt-3 text-xl font-extrabold leading-snug text-background-50 md:text-3xl">
                    {event.title}
                  </h1>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 border-b border-background-200 p-5 md:grid-cols-4 md:p-6">
                {[
                  { icon: "ri-calendar-line", label: "比赛日期", value: formatDate(event.date) },
                  { icon: "ri-time-line", label: "开赛时间", value: event.time },
                  { icon: "ri-map-pin-line", label: "比赛地点", value: event.location },
                  { icon: "ri-eye-line", label: "阅读量", value: `${event.views.toLocaleString()}` },
                ].map((m) => (
                  <div key={m.label} className="flex items-center gap-3">
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-100 text-lg text-primary-700">
                      <i className={m.icon}></i>
                    </span>
                    <div className="min-w-0">
                      <p className="text-xs text-foreground-500">{m.label}</p>
                      <p className="truncate text-sm font-bold text-foreground-900">{m.value}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="p-5 md:p-6">
                <h2 className="flex items-center gap-2 text-lg font-extrabold text-foreground-950">
                  <i className="ri-award-line text-accent-600"></i>
                  双方对阵
                </h2>
                <div className="mt-4 flex flex-col items-center gap-4 rounded-lg border border-background-200 bg-background-100 p-5 sm:flex-row sm:justify-between">
                  <TeamBlock team={event.teams[0]} align="left" />
                  <div className="flex flex-col items-center px-4">
                    <span className="text-2xl font-extrabold text-primary-700 md:text-3xl">{event.score}</span>
                    <span className="mt-1 text-xs text-foreground-500">比分 / 结果</span>
                  </div>
                  <TeamBlock team={event.teams[1]} align="right" />
                </div>
              </div>
            </header>

            <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 text-lg font-extrabold text-foreground-950">
                <i className="ri-article-line text-primary-600"></i>
                赛事实况与详细报道
              </h2>
              <div className="mt-4 rounded-md border-l-4 border-accent-400 bg-accent-50/60 px-4 py-3">
                <p className="text-xs font-bold uppercase tracking-wide text-accent-700">战况简述</p>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-800">{event.highlight}</p>
              </div>
              <div className="mt-4 flex flex-col gap-4">
                {event.body.map((p, i) => (
                  <p key={i} className="text-sm leading-7 text-foreground-700 md:text-[15px]">
                    {p}
                  </p>
                ))}
              </div>
              <div className="mt-5 flex flex-wrap gap-2">
                {event.tags.map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-800"
                  >
                    #{t}
                  </span>
                ))}
              </div>
            </section>

            <section className="mt-6 rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
              <h2 className="flex items-center gap-2 text-lg font-extrabold text-foreground-950">
                <i className="ri-team-line text-primary-600"></i>
                参赛队伍 / 选手资料
              </h2>
              <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
                {event.teams.map((team) => (
                  <div key={team.name} className="rounded-lg border border-background-200 bg-background-100 p-4">
                    <div className="flex items-center gap-3">
                      <TeamBadge name={team.name} short={team.short} />
                      <div>
                        <p className="text-sm font-bold text-foreground-950">{team.name}</p>
                        <p className="text-xs text-foreground-500">{team.recent}</p>
                      </div>
                      <span className="ml-auto rounded-md bg-primary-100 px-2 py-1 text-xs font-bold text-primary-700">
                        排名 {team.rank}
                      </span>
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-foreground-600">{team.note}</p>
                    <div className="mt-3 flex items-center justify-between border-t border-background-200 pt-3">
                      <span className="text-xs text-foreground-500">近期战绩</span>
                      <FormDots form={team.form} />
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to={`/category/${event.categoryId}`}
                className="flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
              >
                <i className="ri-arrow-left-line"></i>
                返回{category?.name}分类页
              </Link>
              <Link
                to="/"
                className="flex items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-5 py-2.5 text-sm font-semibold text-foreground-700 transition-colors hover:bg-background-100"
              >
                <i className="ri-home-4-line"></i>
                返回首页
              </Link>
            </div>
          </article>

          <aside className="min-w-0 flex flex-col gap-6">
            <section className="rounded-lg border border-accent-200 bg-accent-50/60 p-5">
              <h2 className="flex items-center gap-2 text-base font-extrabold text-foreground-950">
                <i className="ri-fire-line text-accent-600"></i>
                今日黑榜表现指标
              </h2>
              {blackEntry ? (
                <div className="mt-4">
                  <div className="flex items-center justify-between rounded-md bg-background-50 p-3">
                    <div>
                      <p className="text-sm font-bold text-foreground-950">{blackEntry.name}</p>
                      <p className="text-xs text-foreground-500">当前黑榜排名 第 {blackEntry.rank} 名</p>
                    </div>
                    <div className="text-right">
                      <p className="text-xs text-foreground-500">mdrs 指数</p>
                      <p className="text-xl font-extrabold text-accent-600">{blackEntry.score.toFixed(1)}</p>
                    </div>
                  </div>
                  <div className="mt-4 flex flex-col gap-3">
                    <MetricBar label="状态" value={blackEntry.metrics.form} />
                    <MetricBar label="防守" value={blackEntry.metrics.defense} />
                    <MetricBar label="纪律" value={blackEntry.metrics.discipline} />
                    <MetricBar label="效率" value={blackEntry.metrics.efficiency} />
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-foreground-600">{blackEntry.comment}</p>
                  <Link
                    to="/blacklist"
                    className="mt-4 flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-4 py-2 text-sm font-bold text-background-50 transition-colors hover:bg-accent-600"
                  >
                    <i className="ri-bar-chart-box-line"></i>
                    查看完整黑榜排行
                  </Link>
                </div>
              ) : (
                <p className="mt-4 rounded-md bg-background-50 p-4 text-sm text-foreground-600">
                  该赛事相关队伍目前未进入今日黑榜。
                  <Link to="/blacklist" className="ml-1 font-semibold text-primary-700 hover:text-primary-800">
                    查看黑榜排行 →
                  </Link>
                </p>
              )}
            </section>

            <section className="rounded-lg border border-background-200 bg-background-50 p-5">
              <SectionHeading title="相关赛事推荐" icon="ri-links-line" />
              <div className="flex flex-col gap-3">
                {related.map((e) => (
                  <Link
                    key={e.id}
                    to={`/detail/${e.id}`}
                    className="group flex items-center gap-3 rounded-md border border-background-200 p-2.5 transition-colors hover:border-primary-300 hover:bg-primary-50/40"
                  >
                    <span className="h-14 w-20 shrink-0 overflow-hidden rounded-md bg-background-200">
                      <img
                        src={e.cover}
                        alt={e.title}
                        className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      />
                    </span>
                    <span className="min-w-0">
                      <span className="line-clamp-2 text-sm font-semibold text-foreground-900 group-hover:text-primary-700">
                        {e.title}
                      </span>
                      <span className="mt-1 block text-xs text-foreground-500">
                        {e.date} · {e.time}
                      </span>
                    </span>
                  </Link>
                ))}
              </div>
            </section>
          </aside>
        </div>

        <section className="mt-12">
          <SectionHeading title="更多赛事资讯" subtitle="来自其他类别的热门赛事" icon="ri-newspaper-line" />
          <div className="flex flex-col gap-4">
            {(events as EventItem[])
              .filter((e) => e.id !== event.id)
              .slice(0, 3)
              .map((e) => (
                <EventRow key={e.id} event={e} />
              ))}
          </div>
        </section>
      </div>
    </Layout>
  );
}

function TeamBlock({
  team,
  align,
}: {
  team: EventItem["teams"][number];
  align: "left" | "right";
}) {
  return (
    <div
      className={`flex items-center gap-3 ${
        align === "right" ? "sm:flex-row-reverse sm:text-right" : ""
      }`}
    >
      <TeamBadge name={team.name} short={team.short} size="lg" />
      <div className={align === "right" ? "sm:text-right" : ""}>
        <p className="text-sm font-bold text-foreground-950 md:text-base">{team.name}</p>
        <p className="text-xs text-foreground-500">联赛排名 {team.rank}</p>
        <div className={`mt-1.5 flex ${align === "right" ? "sm:justify-end" : ""}`}>
          <FormDots form={team.form} />
        </div>
      </div>
    </div>
  );
}